/**
 * Client-side coalescer for content `/info` lookups.
 *
 * A workspace mount renders every ContentNode in the same tick, and each
 * used to fire its own `GET /api/content/<id>/info` — 7+ identical-shape
 * round-trips per open against the slowest endpoint in the S3 profile.
 * `fetchContentInfo` collects the ids that arrive within one short window
 * and issues a single `POST /api/content/info-batch`.
 *
 * Failure semantics match the old per-node fetch: an id the server denies
 * or doesn't know rejects (the node's catch runs its cache fallback), and
 * if the batch endpoint itself is unavailable (e.g. a rolling deploy where
 * an old pod lacks the route) every id falls back to its own single GET.
 */

const BATCH_WINDOW_MS = 25;
/** Server-enforced cap on /api/content/info-batch. */
const BATCH_MAX_IDS = 100;

interface Waiter {
  resolve: (info: Record<string, unknown>) => void;
  reject: (err: Error) => void;
}

let pending: Map<string, Waiter[]> | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;

async function fetchSingle(id: string): Promise<Record<string, unknown>> {
  const r = await fetch(`/api/content/${encodeURIComponent(id)}/info`, {
    credentials: 'same-origin',
  });
  if (!r.ok) throw new Error(`info ${r.status}`);
  return (await r.json()) as Record<string, unknown>;
}

async function flush(): Promise<void> {
  const batch = pending;
  pending = null;
  timer = null;
  if (!batch || batch.size === 0) return;

  const ids = [...batch.keys()];
  const settle = (id: string, info: Record<string, unknown> | null, err?: Error) => {
    for (const w of batch.get(id) ?? []) {
      if (info) w.resolve(info);
      else w.reject(err ?? new Error('info 404'));
    }
  };

  let infos: Record<string, Record<string, unknown> | null> | null = null;
  try {
    const r = await fetch('/api/content/info-batch', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    if (!r.ok) throw new Error(`info-batch ${r.status}`);
    ({ infos } = (await r.json()) as { infos: Record<string, Record<string, unknown> | null> });
  } catch {
    // Batch endpoint unavailable — degrade to per-id GETs so a rolling
    // deploy can never blank every content tile at once.
    await Promise.all(
      ids.map(async (id) => {
        try {
          settle(id, await fetchSingle(id));
        } catch (err) {
          settle(id, null, err instanceof Error ? err : new Error(String(err)));
        }
      }),
    );
    return;
  }

  // The route answers EVERY id it was asked about, null for one the viewer may
  // not read or that is gone: that null is a refusal ("info 404", which the
  // canvas turns into "no longer shared with you"). An answer that says
  // nothing about an id at all is not a refusal, so it rejects with its own
  // message and the node keeps its cache fallback (2026-09-19).
  for (const id of ids) {
    const answered = !!infos && typeof infos === 'object' && Object.prototype.hasOwnProperty.call(infos, id);
    settle(id, answered ? (infos?.[id] ?? null) : null, answered ? undefined : new Error('info unanswered'));
  }
}

/**
 * Resolve the `/info` payload for one content id, transparently batched
 * with every other call made in the same ~25ms window. Rejects where the
 * old per-node fetch would have thrown (non-2xx / missing / denied).
 */
export function fetchContentInfo(id: string): Promise<Record<string, unknown>> {
  return new Promise((resolve, reject) => {
    if (!pending) pending = new Map();
    const waiters = pending.get(id);
    if (waiters) {
      waiters.push({ resolve, reject });
    } else {
      pending.set(id, [{ resolve, reject }]);
    }
    // Flush early when a burst hits the server cap; the next call starts a
    // fresh window.
    if (pending.size >= BATCH_MAX_IDS) {
      if (timer) clearTimeout(timer);
      void flush();
      return;
    }
    if (!timer) timer = setTimeout(() => void flush(), BATCH_WINDOW_MS);
  });
}
